import type { EligibilityFileJobLog } from './eligibility-file-job-log.type.ts';

const canProcessStatuses = ['REQUIRES_USER_APPROVAL', 'REQUIRES_REVIEWER_APPROVAL'];

const canReprocessStatuses = [
  'REQUIRES_USER_APPROVAL',
  'REQUIRES_REVIEWER_APPROVAL',
  'FAILED_VALIDATION',
];

const abortedStatuses = [
  'ABORTED_BY_USER',
  'ABORTED_BY_REVIEWER',
  'ABORTED_BY_ADMIN',
  'ABORTED_FOR_STALENESS',
  'ABORTED_AS_DUPLICATE',
];

export interface EligibilityFileJobLogExtension extends EligibilityFileJobLog {}
export class EligibilityFileJobLogExtension {
  /**************************
   **  Computed Properties **
   **************************/
  get abortedAt() {
    return this.statusHistory.find((status) => abortedStatuses.includes(status.status))?.updatedAt;
  }

  get isRunning() {
    return this.statusHistory.some((update) => update.status === 'EXECUTING_TASKS');
  }

  get isUploading() {
    return this.status === 'WAITING_FOR_UPLOAD';
  }

  get canProcess() {
    return canProcessStatuses.includes(this.status);
  }

  get canReprocess() {
    return canReprocessStatuses.includes(this.status);
  }

  get canSchedule() {
    return this.status === 'REQUIRES_REVIEWER_APPROVAL' || this.status === 'REQUIRES_USER_APPROVAL';
  }

  get canAbort() {
    const additionalStates = ['ERROR_REPORTED', 'FAILED_VALIDATION', 'COMPLETED', 'TEST_COMPLETED'];

    return !abortedStatuses.includes(this.status) && !additionalStates.includes(this.status);
  }

  get hasAborted() {
    return abortedStatuses.includes(this.status);
  }

  get totalProcessed() {
    const { previewCounts, counts } = this;

    if (!previewCounts || !counts) return '0%';

    const preview =
      previewCounts.created +
      previewCounts.updated +
      previewCounts.deactivated +
      previewCounts.reactivated;

    const processed = counts.created + counts.updated + counts.deactivated + counts.reactivated;

    const total = Math.floor((processed / preview) * 100);

    return `${isNaN(total) ? 0 : total}%`;
  }

  get totalUnchanged() {
    const { previewCounts } = this;

    return previewCounts?.unchanged ?? 0;
  }

  get autoProcessEnabled() {
    // This makes a huge assumption that the company relation is loaded
    if (!this.company.companySetting) {
      return true;
    }
    const eligibilityDefault = this.company.companySetting.eligibilityDefault;
    // Prefer the camelCase key tio-api now serves/stores verbatim; fall back to the
    // legacy dasherized key for data served by the pre-opaque formatter.
    return eligibilityDefault.autoProcess ?? eligibilityDefault['auto-process'];
  }
}

const Registration = {
  kind: 'object',
  name: 'eligibility-file-job-log',
  features: EligibilityFileJobLogExtension,
};
export default Registration;
